import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { AreaButton } from '@/main/components/shared/styles';
import { getOptionsStatusActiveGeneral } from '@/main/services/data/data';
import { KeyValue } from '@/main/models/common';
import {
  getDefaultCountryOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  mountReducerWithData,
  removeEmptyValues
} from '@/main/services/utils';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { StatusActiveGeneralEnum } from '@/main/services/enum/StatusActiveGeneralEnum';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import * as S from './styles';
import { TireBrandRequest } from '@/main/models/request/tireBrandRequest';
import ModalTireBrand from '../modalTireBrand/modalTireBrand';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';

interface IFiltersSearchTireBrandProps {
  showFilters: boolean;
  formFilters: TireBrandRequest | null;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  onResetSearch: () => void;
  getDataTireBrand: (filters: TireBrandRequest) => void;
}

const INITIAL_FORM_STATE = {
  country: null as KeyValue | null,
  status: null as KeyValue | null,
  description: '' as string
};

export type IFormFiltersSearchTireBrand = typeof INITIAL_FORM_STATE;

function FiltersSearchTireBrand({
  showFilters,
  setShowFilters,
  getDataTireBrand,
  onResetSearch
}: IFiltersSearchTireBrandProps) {
  const { countriesUser: countryReducer } = useCountryReducer();
  const optionsStatus = getOptionsStatusActiveGeneral();

  const { control, reset, setValue, customHandleSubmit } =
    useFormQueryParams<IFormFiltersSearchTireBrand>({
      defaultValues: INITIAL_FORM_STATE,
      onSubmit: onSubmitFilter,
      mapDefaultValues: {
        country: countryReducer,
        status: mountReducerWithData(optionsStatus)
      }
    });

  const dispatch = useDispatch();

  const optionDefaultCountry = getDefaultCountryOptionDrop(countryReducer.data);
  const optionDefaultStatus = optionsStatus.find(
    item => item.key === StatusActiveGeneralEnum.Active
  )!;

  async function onSubmitFilter(data: IFormFiltersSearchTireBrand) {
    const isStatusActive =
      Number(data.status?.key) === StatusActiveGeneralEnum.Active;

    const mountRequest: Omit<TireBrandRequest, 'pageIndex' | 'pageSize'> =
      removeEmptyValues({
        guidCountry: String(data.country?.key),
        description: data.description,
        ...(!isNullUndefined(data.status) && { status: isStatusActive })
      });

    getDataTireBrand(mountRequest);
    setShowFilters(false);
  }

  function handleClear() {
    reset(INITIAL_FORM_STATE);
    onResetSearch();
    setValue('country', optionDefaultCountry);
    setValue('status', optionDefaultStatus);
  }

  useEffect(() => {
    dispatch(getCountriesUser());
  }, []);

  const disabledCountry = countryReducer?.data?.length === 1;

  return (
    <Box showBox={showFilters} title={getTranslateByKey('information.filters')}>
      <form onSubmit={customHandleSubmit} noValidate>
        <S.Container>
          <KogAutoCompleteControlled
            id="id-searchTireBrand-select-country"
            nameCustom="country"
            label={getTranslateByKey('information.country')}
            isDefaultValueUnique
            control={control}
            rulesCustom={{ required: true }}
            setValueCustom={setValue}
            options={countryReducer.data}
            isLoading={countryReducer.isLoading}
            disabled={disabledCountry}
            onChangeCustom={() => {}}
          />

          <KogInputTextControlledNew
            id="id-tireBrand-txt-tireBrand"
            name="description"
            label={getTranslateByKey('information.tireBrand')}
            control={control}
          />

          <KogAutoCompleteControlled
            id="id-searchIntegration-select-status"
            nameCustom="status"
            label={getTranslateByKey('information.status')}
            valueDefaultKey={optionDefaultStatus?.key}
            control={control}
            rulesCustom={{ required: false }}
            setValueCustom={setValue}
            options={optionsStatus}
            disabled={false}
            onChangeCustom={() => {}}
          />
          <S.AreaButtons>
            <SecondaryButton id="id-tireBrand-btn-submit" type="submit">
              {getTranslateByKey('information.search')}
            </SecondaryButton>
            <SecondaryButton
              id="id-tireBrand-btn-clear"
              type="button"
              onClick={handleClear}>
              {getTranslateByKey('information.clear')}
            </SecondaryButton>
          </S.AreaButtons>
        </S.Container>
      </form>
    </Box>
  );
}

function FiltersTireBrandWrapper(
  props: Omit<IFiltersSearchTireBrandProps, 'showFilters' | 'setShowFilters'>
) {
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [isOpenModalTireBrand, setIsOpenModalTireBrand] =
    useState<boolean>(false);

  function handleFilters() {
    setShowFilters(prev => !prev);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  
  function handleNewTireBrand(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    e.preventDefault();
    e.stopPropagation();
    setIsOpenModalTireBrand(true);
  }

  function refreshSearchGrid() {
    const currentFilters = props.formFilters;
    if (!isNullUndefined(currentFilters)) {
      props.getDataTireBrand(currentFilters);
    }
  }

  const mergedProps: IFiltersSearchTireBrandProps = {
    ...props,
    showFilters,
    setShowFilters
  };

  return (
    <>
      <AreaButton>
        <Button onClick={handleFilters} id="id-tireBrand-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
        <Button onClick={handleNewTireBrand} id="id-tireBrand-new-btn-filters">
          {getTranslateByKey('information.newTireBrand')}
        </Button>
      </AreaButton>
      <FiltersSearchTireBrand {...mergedProps} />

      <ModalTireBrand
        isOpenModal={isOpenModalTireBrand}
        closeModal={() => setIsOpenModalTireBrand(false)}
        setIsOpenModal={setIsOpenModalTireBrand}
        refreshSearchGrid={refreshSearchGrid}
      />
    </>
  );
}

export default FiltersTireBrandWrapper;
